/**
 * What the egg is doing, given where the build is.
 *
 * The design derived all of this from `frac` — a 0-to-1 number produced by an
 * 18-second timer with a hardcoded script behind it. Nothing real emits that.
 * What Hatch actually has is `BuildPhase`, advancing on genuine events at
 * wildly uneven intervals, so every value the prototype read off the clock is
 * read off the phase here instead.
 *
 * The six phases and the design's six stages line up exactly, which is what
 * makes the substitution clean rather than a compromise: the state machine is
 * ours, the copy is the design's.
 *
 * Pure and total over `BuildPhase`. Typed as a full `Record`, so adding a phase
 * to the contract is a type error here rather than a pill that silently never
 * lights.
 */

import type { BuildPhase } from "@hatch/shared";
import { BUILD_PHASES, PHASE_ORDER } from "@hatch/shared";
import { PHASE_LABELS } from "../../lib/labels.js";

export type PillState = "done" | "active" | "pending" | "halted";

export interface Pill {
  label: string;
  state: PillState;
  /** Only ever true for a live active pill — see `stageFor`. */
  pulse: boolean;
}

export interface Stage {
  /** Position in `BUILD_PHASES`, which is also the index of the active pill. */
  index: number;
  /** 0-4. Indexes how many of the four shell shards are showing. */
  crackLevel: number;
  /** A CSS `animation` shorthand, or `none`. */
  eggAnimation: string;
  /** Heat behind the shell, from the third crack on. */
  glow: boolean;
  statusTitle: string;
  statusSub: string;
  pills: Pill[];
}

/**
 * The design's copy, re-keyed from its simulated stages onto our real phases.
 *
 * The cute names the design gave these stages — Nesting, Blueprint, Backbone,
 * Plumage, Pipping, Hatched — used to be the pill labels too. They are not any
 * more: the stage tracker now reads from `PHASE_LABELS`, the same six words the
 * classic timeline uses, so the two skins name the build's progress identically
 * and a user moving between them is not learning a second vocabulary for the
 * same five minutes.
 *
 * What the design's voice keeps is this: the status line beside the egg, which
 * is where the charm belongs. "Growing plumage…" over a cracking egg is the
 * skin having a personality; "Plumage" as the label on a progress tracker was
 * the skin withholding which phase the build is actually in.
 *
 * `queued` keeps its pill rather than being hidden the way the classic timeline
 * hides it as bookkeeping. Hiding it would leave nothing marked active for the
 * whole of the wait — while the egg wobbles and the status says the workspace
 * is being fluffed up — and a tracker with no current step reads as a tracker
 * that has not started.
 */
const STAGE_COPY: Record<BuildPhase, { title: string; sub: string; crack: number }> = {
  queued: {
    title: "Nesting…",
    sub: "Fluffing up an ephemeral workspace and reading the house rules.",
    crack: 0,
  },
  scaffolding: {
    title: "Blueprinting…",
    sub: "Deciding what this creature needs: pages, tables, endpoints.",
    crack: 0,
  },
  backend: {
    title: "Growing a backbone…",
    sub: "Laying database tables and wiring the API — sturdy bones first.",
    crack: 1,
  },
  frontend: {
    title: "Growing plumage…",
    sub: "The interface takes shape. Fonts, colors, and a healthy coat of CSS.",
    crack: 2,
  },
  deploying: {
    title: "Pipping!",
    sub: "First cracks. Deploying to the wild — almost through the shell.",
    crack: 3,
  },
  done: {
    title: "Hatched!",
    sub: "It's alive.",
    crack: 4,
  },
};

/**
 * The design's wobble escalation, indexed by crack level.
 *
 * More cracks, harder struggle — the one place the egg is allowed to imply
 * effort. The last two are `wob3`, which holds still and then shudders; a
 * continuous rock at that speed reads as vibration, while an intermittent one
 * reads as something kicking.
 */
const WOBBLE = [
  "wob1 2.4s ease-in-out infinite",
  "wob1 1.6s ease-in-out infinite",
  "wob2 1.2s ease-in-out infinite",
  "wob3 1.6s linear infinite",
  "wob3 1.1s linear infinite",
] as const;

/** Crack level at which the shell starts glowing from inside. */
const GLOW_FROM_CRACK = 3;

/**
 * Every pill done, nothing pulsing — the tracker on a build that is over and
 * produced a site.
 *
 * The reveal keeps the building screen's tracker rather than dropping it, and
 * reading it straight off `stageFor` would leave the last pill pulsing beside a
 * finished site. Marking only the active pill done is not enough either: the
 * terminal event can arrive while `phase` still says `deploying`, which would
 * leave "Done" showing as pending on a screen framing the deployed site.
 */
export function settledPills(pills: Pill[]): Pill[] {
  return pills.map((pill) => ({ ...pill, state: "done", pulse: false }));
}

export function stageFor(phase: BuildPhase, failed: boolean): Stage {
  // Typed as total over `BuildPhase`, but the value crossing the wire is only
  // as trustworthy as the API sending it: a frontend running against a newer
  // API mid-rollout can be handed a phase this build has never heard of. The
  // types say that cannot happen, so nothing would catch it, and the cost of
  // being wrong is a dereference of `undefined` during render — which, with no
  // error boundary above it, is a white page in the middle of someone's build.
  // The classic timeline degrades to "nothing highlighted" on the same input;
  // this degrades to "still nesting", which is the same shape of answer.
  const known = phase in STAGE_COPY ? phase : "queued";
  const index = PHASE_ORDER[known];
  const copy = STAGE_COPY[known];

  return {
    index,
    crackLevel: copy.crack,
    // A failed build stops moving. The cracks stay at the level it reached —
    // that damage is real and undoing it would misreport how far it got — but
    // an egg still struggling under a failure message says the build is fine.
    eggAnimation: failed ? "none" : (WOBBLE[copy.crack] ?? WOBBLE[0]),
    // Same reasoning: the glow reads as heat behind a live shell.
    glow: !failed && copy.crack >= GLOW_FROM_CRACK,
    statusTitle: copy.title,
    statusSub: copy.sub,
    pills: BUILD_PHASES.map((p) => {
      const at = PHASE_ORDER[p];
      const state: PillState =
        at < index ? "done" : at > index ? "pending" : failed ? "halted" : "active";
      return { label: PHASE_LABELS[p], state, pulse: state === "active" };
    }),
  };
}
